import React from 'react';
import {
  Badge, Button, Card, Col, ProgressBar, Row, Table,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  ValidateDocumentoItem,
  ValidateDocumentsEstado,
  ValidateDocumentsResumen,
} from 'hooks/useValidateDocuments';

type ValidateDocumentsPanelProps = {
  estado: ValidateDocumentsEstado;
  progreso: number;
  total: number;
  documentos: ValidateDocumentoItem[];
  resumen: ValidateDocumentsResumen | null;
  errorMessage: string | null;
  puedeEnviar: boolean;
  onValidate: () => void;
  translateDocStatus: ( status: string ) => string;
  hasDocumentSelection: boolean;
};

function getStatusBadgeVariant( status: string ): string {
  if ( status === 'OK' ) return 'success';
  if ( status.startsWith( 'WARNING_' ) ) return 'warning';
  if ( status.startsWith( 'ERROR_' ) ) return 'danger';
  return 'secondary';
}

function ValidateDocumentsPanel( {
  estado,
  progreso,
  total,
  documentos,
  resumen,
  errorMessage,
  puedeEnviar,
  onValidate,
  translateDocStatus,
  hasDocumentSelection,
}: ValidateDocumentsPanelProps ) {
  const { t } = useTranslation();

  const percentage = total > 0 ? Math.round( ( progreso / total ) * 100 ) : 0;

  return (
    <Card className="mt-4">
      <Card.Title className="p-2 text--sm bg-light rounded-top">
        {t( 'admin.invitations.validateDocuments.title' )}
      </Card.Title>
      <Card.Body className="px-3 py-3">
        {estado === 'idle' && (
          <Row className="align-items-center">
            <Col>
              <p className="text-muted small mb-0">
                {t( 'admin.invitations.validateDocuments.hint' )}
              </p>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={onValidate}
                disabled={!hasDocumentSelection}
              >
                {t( 'admin.invitations.validateDocuments.button' )}
              </Button>
            </Col>
          </Row>
        )}

        {estado === 'processing' && (
          <div>
            <span className="text--gray20 text--sm">
              {t( 'admin.invitations.validateDocuments.processing' )}
              {' '}
              {progreso}
              /
              {total}
              {' '}
              (
              {percentage}
              %)
            </span>
            <ProgressBar
              now={percentage}
              animated
              striped
              className="mt-2"
              style={{ height: '10px' }}
            />
          </div>
        )}

        {errorMessage && (
          <div className="invalid-feedback d-block mt-2">
            {errorMessage}
          </div>
        )}

        {estado === 'completed' && resumen && (
          <div className="mt-2">
            <Row className="mb-3">
              <Col sm={3}>
                <span className="text-muted small d-block">
                  {t( 'admin.invitations.validateDocuments.summaryValid' )}
                </span>
                <strong>{resumen.validos}</strong>
              </Col>
              <Col sm={3}>
                <span className="text-muted small d-block">
                  {t( 'admin.invitations.validateDocuments.summaryWarnings' )}
                </span>
                <strong className={resumen.advertencias && resumen.advertencias > 0 ? 'text-warning' : undefined}>
                  {resumen.advertencias ?? 0}
                </strong>
              </Col>
              <Col sm={3}>
                <span className="text-muted small d-block">
                  {t( 'admin.invitations.validateDocuments.summaryInvalid' )}
                </span>
                <strong className={resumen.invalidos > 0 ? 'text-danger' : undefined}>
                  {resumen.invalidos}
                </strong>
              </Col>
              <Col sm={3}>
                {puedeEnviar ? (
                  <Badge bg="success">
                    {t( 'admin.invitations.validateDocuments.readyToSend' )}
                  </Badge>
                ) : (
                  <Badge bg="warning" text="dark">
                    {t( 'admin.invitations.validateDocuments.blockedSend' )}
                  </Badge>
                )}
              </Col>
            </Row>

            {documentos.length > 0 && (
              <Table responsive size="sm" className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>{t( 'admin.invitations.validateDocuments.colName' )}</th>
                    <th>{t( 'admin.invitations.validateDocuments.colStatus' )}</th>
                    <th>{t( 'admin.invitations.validateDocuments.colDetail' )}</th>
                  </tr>
                </thead>
                <tbody>
                  {documentos.map( ( doc ) => (
                    <tr key={`${doc.nombre}-${doc.urlSharepoint ?? ''}`}>
                      <td className="small">{doc.nombre}</td>
                      <td>
                        <Badge
                          bg={getStatusBadgeVariant( doc.estado )}
                          text={doc.estado.startsWith( 'WARNING_' ) ? 'dark' : undefined}
                        >
                          {translateDocStatus( doc.estado )}
                        </Badge>
                      </td>
                      <td className="small text-muted">
                        {doc.estado !== 'OK' && doc.detalle ? doc.detalle : '—'}
                      </td>
                    </tr>
                  ) )}
                </tbody>
              </Table>
            )}

            {!puedeEnviar && (
              <p className="text-danger small mt-3 mb-0">
                {t( 'admin.invitations.validateDocuments.cannotSendUntilFixed' )}
              </p>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default ValidateDocumentsPanel;
